import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import MainSection from "./MainSection";
import { Container, Grid, Icon } from "semantic-ui-react";
import styles from "./PatternsList.module.scss";
import SortSection from "../CategoryPage/SortSection/SortSection";
import FilterSidebar from "./FilterSidebar";
import { FormattedMessage } from "react-intl";

const PatternsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getArrayParam = useCallback((key: string): string[] => {
    const value = searchParams.get(key);
    return value ? value.split(",") : [];
  }, [searchParams]);

  const [sortBy, setSortBy] = useState<string>(
    searchParams.get('sortBy') || ''
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc'
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(getArrayParam('categories'));
  const [selectedClients, setSelectedClients] = useState<string[]>(getArrayParam('clients'));
  const [selectedFabrics, setSelectedFabrics] = useState<string[]>(getArrayParam('fabrics'));
  const [selectedSizes, setSelectedSizes] = useState<string[]>(getArrayParam('sizes'));
  const [resultsCount, setResultsCount] = useState<number | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedClients.length > 0 ||
    selectedFabrics.length > 0 ||
    selectedSizes.length > 0;

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedClients([]);
    setSelectedFabrics([]);
    setSelectedSizes([]);
    // Clear the corresponding search parameters in the URL
    searchParams.delete("categories");
    searchParams.delete("clients");
    searchParams.delete("fabrics");
    searchParams.delete("sizes");
    
    setSearchParams(searchParams);
  };

  // Helper to compare array state and avoid unnecessary updates when syncing from URL
  const shouldUpdateArrayState = (
    newValue: string[],
    currentValue: string[]
  ) => {
    return (
      newValue.length > 0 &&
      JSON.stringify(newValue) !== JSON.stringify(currentValue)
    );
  };

  // Scroll to top when component mounts (e.g. when navigating from HomePage or ShopByStyle)
  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Sync local state from URL when navigation occurs (e.g. ProductMenu/CategoryChips click)
  useEffect(() => {
    const newCategories = getArrayParam('categories');
    const newClients = getArrayParam('clients');
    const newFabrics = getArrayParam('fabrics');
    const newSizes = getArrayParam('sizes');

    if (shouldUpdateArrayState(newCategories, selectedCategories)) {
      setSelectedCategories(newCategories);
    }

    if (shouldUpdateArrayState(newClients, selectedClients)) {
      setSelectedClients(newClients);
    }

    if (shouldUpdateArrayState(newFabrics, selectedFabrics)) {
      setSelectedFabrics(newFabrics);
    }

    if (shouldUpdateArrayState(newSizes, selectedSizes)) {
      setSelectedSizes(newSizes);
    }

    const urlSortBy = searchParams.get('sortBy');
    if (urlSortBy !== null) {
      setSortBy(urlSortBy);
    }

    const urlSortOrder = searchParams.get('sortOrder');
    if (urlSortOrder === 'asc' || urlSortOrder === 'desc') {
      setSortOrder(urlSortOrder);
    }
  }, [searchParams, getArrayParam, selectedCategories, selectedClients, selectedFabrics, selectedSizes]);

  return (
    <div className={styles.patternsPage}>
      <Container>
        <SortSection sortBy={sortBy} setSortBy={setSortBy} sortOrder={sortOrder} setSortOrder={setSortOrder} />
        <Grid>
          <Grid.Column width={4} only="computer">
            <FilterSidebar
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              selectedClients={selectedClients}
              setSelectedClients={setSelectedClients}
              selectedFabrics={selectedFabrics}
              setSelectedFabrics={setSelectedFabrics}
              selectedSizes={selectedSizes}
              setSelectedSizes={setSelectedSizes}
            />
          </Grid.Column>
          <Grid.Column mobile={16} tablet={16} only="tablet mobile" className={styles.mobileFiltersColumn}>
            <div className={styles.mobileFiltersHeader}>
              <button
                type="button"
                className={styles.mobileFiltersToggle}
                onClick={() => setMobileFiltersOpen((prev) => !prev)}
              >
                <span className={styles.mobileFiltersToggleLabel}>
                  <Icon name={mobileFiltersOpen ? "angle up" : "filter"} />
                  <FormattedMessage id="patterns.filters.title" defaultMessage="Filters" />
                </span>
              </button>
              <button
                type="button"
                className={styles.mobileFiltersClear}
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                <FormattedMessage
                  id="patterns.filters.clearFilters"
                  defaultMessage="Clear filters"
                />
              </button>
            </div>
            {typeof resultsCount === "number" && (
              <div className={styles.mobileFiltersResults}>
                <span className={styles.mobileFiltersCount}>
                  <FormattedMessage
                    id="patterns.results.short"
                    defaultMessage="{count} results"
                    values={{ count: resultsCount }}
                  />
                </span>
              </div>
            )}
            {mobileFiltersOpen && (
              <div className={styles.mobileFiltersPanel}>
                <FilterSidebar
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  selectedClients={selectedClients}
                  setSelectedClients={setSelectedClients}
                  selectedFabrics={selectedFabrics}
                  setSelectedFabrics={setSelectedFabrics}
                  selectedSizes={selectedSizes}
                  setSelectedSizes={setSelectedSizes}
                  resultsCount={resultsCount}
                  isSticky={false}
                  showHeader={false}
                />
              </div>
            )}
          </Grid.Column>
          <Grid.Column mobile={16} tablet={16} computer={12}>
            <MainSection
              sortBy={sortBy}
              sortOrder={sortOrder}
              selectedCategories={selectedCategories}
              selectedClients={selectedClients}
              selectedFabrics={selectedFabrics}
              selectedSizes={selectedSizes}
              onResultsCountChange={setResultsCount}
            />
          </Grid.Column>
        </Grid>
      </Container>
    </div>
  );
};

export default PatternsList;
